// Contrato de POST /dashboard?rel= (CLAUDE.md da API, "Dashboards (contrato padrão)").
// O mesmo que o app tifiregerencial consome (schemas/dashboard.ts de lá).

export type Formato = 'moeda' | 'numero' | 'percentual' | 'texto';

export interface ItemValor {
  rotulo: string;
  valor: number;
}

export interface Serie {
  nome: string;
  pontos: ItemValor[];
}

interface WidgetBase {
  id: string;
  titulo: string;
  formato: Formato;
}

export interface WidgetKpi extends WidgetBase {
  tipo: 'kpi';
  valor: number;
  tom: 'positivo' | 'negativo' | 'neutro';
  detalhe?: string;
}

export interface WidgetRosca extends WidgetBase {
  tipo: 'rosca';
  itens: ItemValor[];
}

export interface WidgetBarra extends WidgetBase {
  tipo: 'barra';
  itens?: ItemValor[];
  series?: Serie[];
}

export interface WidgetLinha extends WidgetBase {
  tipo: 'linha';
  series: Serie[];
}

export interface ColunaTabela {
  campo: string;
  caption: string;
  formato: Formato;
}

export interface WidgetTabela extends WidgetBase {
  tipo: 'tabela';
  colunas: ColunaTabela[];
  linhas: Record<string, string | number>[];
}

export type Widget = WidgetKpi | WidgetRosca | WidgetBarra | WidgetLinha | WidgetTabela;

export interface Dashboard {
  titulo: string;
  periodo?: string;
  widgets: Widget[];
  cabecalho?: { titulo?: string; empresa?: string; filial?: string; emissao?: string };
}

const FORMATOS: Formato[] = ['moeda', 'numero', 'percentual', 'texto'];

function formato(valor: unknown): Formato {
  return FORMATOS.includes(valor as Formato) ? (valor as Formato) : 'numero';
}

function ehItem(i: any): i is ItemValor {
  return !!i && typeof i.rotulo === 'string' && typeof i.valor === 'number';
}

function ehSerie(s: any): s is Serie {
  return !!s && typeof s.nome === 'string' && Array.isArray(s.pontos) && s.pontos.every(ehItem);
}

function listaDe<T>(valor: unknown, valida: (v: any) => v is T): T[] | undefined {
  return Array.isArray(valor) && valor.every(valida) ? valor : undefined;
}

// Valida um widget vindo da API. Tipo novo ou inválido devolve null (é descartado).
function lerWidget(bruto: any): Widget | null {
  if (!bruto || typeof bruto.id !== 'string' || typeof bruto.titulo !== 'string') return null;
  const base = { id: bruto.id, titulo: bruto.titulo, formato: formato(bruto.formato) };

  switch (bruto.tipo) {
    case 'kpi': {
      if (typeof bruto.valor !== 'number') return null;
      const tom = ['positivo', 'negativo'].includes(bruto.tom) ? bruto.tom : 'neutro';
      return { ...base, tipo: 'kpi', valor: bruto.valor, tom, detalhe: typeof bruto.detalhe === 'string' ? bruto.detalhe : undefined };
    }
    case 'rosca': {
      const itens = listaDe(bruto.itens, ehItem);
      return itens?.length ? { ...base, tipo: 'rosca', itens } : null;
    }
    case 'barra': {
      const itens = listaDe(bruto.itens, ehItem);
      const series = listaDe(bruto.series, ehSerie);
      return itens?.length || series?.length ? { ...base, tipo: 'barra', itens, series } : null;
    }
    case 'linha': {
      const series = listaDe(bruto.series, ehSerie);
      return series?.length ? { ...base, tipo: 'linha', series } : null;
    }
    case 'tabela': {
      if (!Array.isArray(bruto.colunas) || !Array.isArray(bruto.linhas)) return null;
      const colunas: ColunaTabela[] = bruto.colunas
        .filter((c: any) => c && typeof c.campo === 'string')
        .map((c: any) => ({ campo: c.campo, caption: String(c.caption ?? c.campo), formato: formato(c.formato) }));
      return { ...base, tipo: 'tabela', colunas, linhas: bruto.linhas };
    }
    default:
      return null;
  }
}

export function lerDashboard(dados: any): Dashboard {
  const dash = dados?.dashboard;
  if (!dash || !Array.isArray(dash.widgets)) throw new Error('Resposta do dashboard em formato inesperado');

  const widgets: Widget[] = [];
  for (const bruto of dash.widgets) {
    const w = lerWidget(bruto);
    if (w) widgets.push(w);
    else console.warn('Widget ignorado', bruto);
  }

  return { titulo: String(dash.titulo ?? ''), periodo: dash.periodo, widgets, cabecalho: dados.cabecalho };
}
